import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  Modal,
  IconButton,
  CircularProgress,
  alpha,
  useTheme,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import proxy from '../api/axios';

const ACCENT = '#137274';
const PINK = '#f50057';

const ROLE_LABEL = {
  'problem-setter': 'Problem Setter',
  'content-writer': 'Content Writer',
  'dsa-mentor': 'DSA Mentor / Teacher',
  'swe-intern': 'Software Engineer Intern',
  'sde-1': 'Software Engineer (SDE-1)',
};

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  linkedin: '',
  portfolio: '',
  location: '',
  experience: '',
  coverLetter: '',
};

const CareersPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)';

  const [openings, setOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [role, setRole] = useState('problem-setter');
  const [form, setForm] = useState(emptyForm);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await proxy.get('/careers/openings');
        setOpenings(data.openings || []);
      } catch {
        toast.error('Could not load openings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const openApply = (roleId) => {
    setRole(roleId);
    setForm(emptyForm);
    setResume(null);
    setDone(false);
    setModalOpen(true);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Resume must be under 5MB');
      return;
    }
    setResume(file);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.coverLetter.trim()) {
      toast.error('Please fill name, email, phone, and a short note');
      return;
    }
    if (!resume) {
      toast.error('Please attach your resume (PDF or Word)');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('role', role);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('resume', resume);

      await proxy.post('/careers/apply', fd);
      setDone(true);
      toast.success('Application submitted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not submit application');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg }}>
      <Helmet>
        <title>Careers | CrafterHack</title>
        <meta
          name="description"
          content="Join CrafterHack — openings for Problem Setters, Content Writers, DSA Mentors, Interns, and SDE-1."
        />
      </Helmet>

      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 5 },
          borderBottom: `1px solid ${border}`,
          background: isDark
            ? `radial-gradient(800px 360px at 15% -10%, ${alpha(ACCENT, 0.35)}, transparent 55%), #0f1419`
            : `radial-gradient(800px 360px at 10% -20%, ${alpha(ACCENT, 0.16)}, transparent 55%), linear-gradient(180deg, #e8f4f5 0%, #f7f9fb 100%)`,
        }}
      >
        <Box sx={{ maxWidth: 960, mx: 'auto' }}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Chip
              icon={<WorkOutlineIcon sx={{ fontSize: '16px !important' }} />}
              label="We're hiring"
              sx={{
                mb: 1.75,
                fontWeight: 800,
                bgcolor: alpha(ACCENT, isDark ? 0.28 : 0.14),
                color: ACCENT,
                '& .MuiChip-icon': { color: ACCENT },
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2rem', sm: '2.5rem' },
                letterSpacing: '-0.04em',
                mb: 1,
              }}
            >
              Careers at{' '}
              <Box component="span" sx={{ color: ACCENT }}>
                CrafterHack
              </Box>
            </Typography>
            <Typography sx={{ color: muted, maxWidth: 520, fontSize: '1.02rem', lineHeight: 1.55 }}>
              Help engineers get interview-ready — build problems, content, mentorship, and the
              product itself. Remote-friendly roles.
            </Typography>
          </motion.div>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3.5, md: 5 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: ACCENT }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {openings.map((job, i) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.05, 0.3) }}
              >
                <Box
                  sx={{
                    p: { xs: 2.25, sm: 2.75 },
                    borderRadius: 3,
                    border: `1px solid ${border}`,
                    bgcolor: surface,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2.5,
                    alignItems: { md: 'flex-start' },
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                      <Chip
                        size="small"
                        label={job.team}
                        sx={{
                          height: 22,
                          fontWeight: 800,
                          fontSize: '0.65rem',
                          bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
                          color: ACCENT,
                        }}
                      />
                      <Chip
                        size="small"
                        label={job.type}
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04),
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: '1.2rem',
                        letterSpacing: '-0.02em',
                        mb: 0.75,
                      }}
                    >
                      {job.title}
                    </Typography>
                    <Typography
                      sx={{ color: muted, fontSize: '0.9rem', lineHeight: 1.55, mb: 1.5, maxWidth: 620 }}
                    >
                      {job.summary}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                      {(job.highlights || []).map((h) => (
                        <Box key={h} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <CheckCircleIcon sx={{ fontSize: 15, color: ACCENT, mt: 0.25 }} />
                          <Typography variant="body2" sx={{ color: muted, fontSize: '0.82rem' }}>
                            {h}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => openApply(job.id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      bgcolor: ACCENT,
                      flexShrink: 0,
                      alignSelf: { xs: 'stretch', md: 'center' },
                      '&:hover': { bgcolor: '#0f5c5e' },
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </motion.div>
            ))}
          </Box>
        )}

        <Typography
          variant="body2"
          sx={{ color: muted, mt: 4, textAlign: 'center', fontSize: '0.85rem' }}
        >
          Don’t see a perfect fit? Email{' '}
          <Box component="a" href="mailto:updates@crafterhack.com" sx={{ color: ACCENT, fontWeight: 700 }}>
            updates@crafterhack.com
          </Box>{' '}
          with your resume.
        </Typography>
      </Box>

      <Modal open={modalOpen} onClose={() => !submitting && setModalOpen(false)}>
        <Box
          sx={{
            width: { xs: '94%', sm: 520 },
            maxHeight: '92vh',
            overflow: 'auto',
            bgcolor: isDark ? '#0f1419' : '#f7f9fb',
            borderRadius: 3,
            border: `1px solid ${border}`,
            boxShadow: isDark
              ? '0 24px 64px rgba(0,0,0,0.55)'
              : '0 24px 64px rgba(15,60,70,0.18)',
            margin: 'auto',
            mt: { xs: '4%', sm: '5%' },
            position: 'relative',
            p: { xs: 2.25, sm: 3 },
          }}
        >
          <IconButton
            onClick={() => setModalOpen(false)}
            disabled={submitting}
            sx={{ position: 'absolute', top: 10, right: 10 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {done ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#1a7f37', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', mb: 1 }}>
                Application received
              </Typography>
              <Typography sx={{ color: muted, mb: 2.5 }}>
                Thanks — we’ll review your profile and reach out if there’s a fit.
              </Typography>
              <Button
                variant="contained"
                onClick={() => setModalOpen(false)}
                sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT }}
              >
                Close
              </Button>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontWeight: 900, fontSize: '1.25rem', letterSpacing: '-0.02em', mb: 0.5, pr: 4 }}>
                Apply · {ROLE_LABEL[role] || role}
              </Typography>
              <Typography variant="body2" sx={{ color: muted, mb: 2.5 }}>
                Attach a resume (PDF/Word, max 5MB) and tell us why you’re a fit.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  select
                  size="small"
                  label="Role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  {Object.entries(ROLE_LABEL).map(([id, label]) => (
                    <MenuItem key={id} value={id}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Full name"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
                <TextField
                  size="small"
                  label="Email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
                <TextField
                  size="small"
                  label="Phone / WhatsApp"
                  required
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
                <TextField
                  size="small"
                  label="LinkedIn"
                  value={form.linkedin}
                  onChange={(e) => setForm((f) => ({ ...f, linkedin: e.target.value }))}
                />
                <TextField
                  size="small"
                  label="Portfolio / GitHub"
                  value={form.portfolio}
                  onChange={(e) => setForm((f) => ({ ...f, portfolio: e.target.value }))}
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    size="small"
                    label="Location"
                    fullWidth
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  />
                  <TextField
                    size="small"
                    label="Experience"
                    fullWidth
                    placeholder="e.g. 2 years / Fresher"
                    value={form.experience}
                    onChange={(e) => setForm((f) => ({ ...f, experience: e.target.value }))}
                  />
                </Box>
                <TextField
                  size="small"
                  label="Why CrafterHack? / cover note"
                  required
                  multiline
                  minRows={3}
                  value={form.coverLetter}
                  onChange={(e) => setForm((f) => ({ ...f, coverLetter: e.target.value }))}
                />

                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileOutlinedIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: border,
                    color: 'inherit',
                    justifyContent: 'flex-start',
                    py: 1.25,
                  }}
                >
                  {resume ? resume.name : 'Upload resume (PDF / Word)'}
                  <input hidden type="file" accept=".pdf,.doc,.docx,application/pdf" onChange={onFile} />
                </Button>

                <Button
                  variant="contained"
                  onClick={submit}
                  disabled={submitting}
                  endIcon={!submitting ? <ArrowForwardIcon /> : undefined}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    bgcolor: PINK,
                    py: 1.25,
                    mt: 0.5,
                    '&:hover': { bgcolor: '#c40048' },
                  }}
                >
                  {submitting ? 'Submitting…' : 'Submit application'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default CareersPage;
